import React from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import ReCAPTCHA from 'react-google-recaptcha'
import { FormErrorMessage } from '@spin-dd/gatsby-theme-fujimoto/src/components/FormErrorMessage'

const reCAPTCHASiteKey = process.env.GATSBY_SITE_RECAPTCHA_KEY

function ContactFormSchoolOpenCampusInput({
  onConfirm,
  privacyPolicyPath,
  recruitmentPath,
  className,
  openCampusDates,
  requireGrade,
}) {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useFormContext()

  const recaptchaRef = React.useRef()
  const onVerifyCaptcha = (token) => {
    // expire した場合には null が返ってくる
    setValue('g-recaptcha-response', token ?? '', { shouldValidate: true })
  }

  // 「高等学校ご学年」は「どちらかお選びください」が「在学中」の場合に表示する
  const graduatedType = useWatch({ name: 'graduatedType' })
  const showGradeFields = requireGrade(graduatedType)
  if (graduatedType === '卒業') {
    setValue('grade', '')
  }

  // submitHandlers
  const onValid = () => {
    onConfirm()
  }

  // お問い合わせの項目
  const types = [
    'オープンキャンパスのお申込み',
    'オープンキャンパスのお問い合わせ',
  ]
  // お問い合わせいただいた方とご本人さまとのご関係
  const relations = ['ご本人', 'ご両親・祖父母', '親族', '学校関係者', 'その他']
  // 高等学校名（どちらかお選びください）
  const graduatedTypes = ['在学中', '卒業']
  // 学年
  const grades = ['1年', '2年', '3年']

  return (
    <>
      <h2 className={className}>オープンキャンパスのお問い合わせ</h2>
      <div className="inquiry_area">
        <p className="note">
          <span className="red">※</span>は必須項目です。
        </p>
        <form name="contact" method="post" onSubmit={handleSubmit(onValid)}>
          <input type="hidden" {...register('g-recaptcha-response')} />
          <input type="hidden" {...register('csrftoken')} />
          <table className="contact">
            <tbody>
              <tr>
                <th className="must">お問い合わせの項目</th>
                <td className="checkbox_square">
                  {types.map((type, index) => (
                    <label key={index}>
                      <input
                        type="checkbox"
                        {...register('inquiryType')}
                        value={type}
                      />
                      <span>{type}</span>
                    </label>
                  ))}
                  <FormErrorMessage error={errors.inquiryType} />
                  <div className="recruitment-link">
                    採用についてのお問い合わせは
                    <a href={recruitmentPath}>こちら</a>
                    からお問い合わせください。
                  </div>
                </td>
              </tr>
              <tr>
                <th className="must">お名前（漢字）</th>
                <td className="name">
                  <span>
                    <input
                      type="text"
                      {...register('nameKanji')}
                      placeholder="例：藤元太郎"
                    />
                  </span>
                  <FormErrorMessage error={errors.nameKanji} />
                </td>
              </tr>
              <tr>
                <th className="must">お名前（フリガナ）</th>
                <td className="name">
                  <span>
                    <input
                      type="text"
                      {...register('nameFurigana')}
                      placeholder="例：フジモトタロウ"
                    />
                  </span>
                  <FormErrorMessage error={errors.nameFurigana} />
                </td>
              </tr>
              <tr>
                <th className="must">
                  お問い合わせいただいた方とご本人さまとのご関係
                </th>
                <td className="radio_btn">
                  {relations.map((relation, index) => (
                    <label key={index}>
                      <input
                        type="radio"
                        {...register('relationship')}
                        value={relation}
                      />
                      <span>{relation}</span>
                    </label>
                  ))}
                  <FormErrorMessage error={errors.relationship} />
                </td>
              </tr>
              <tr>
                <th className="must">メールアドレス</th>
                <td className="mail cont">
                  <span>
                    <input
                      type="email"
                      {...register('email', {
                        deps: 'emailConfirm',
                      })}
                      placeholder="例：taro@fujimoto.or.jp"
                    />
                  </span>
                  <FormErrorMessage error={errors.email} />
                  <span>
                    <input
                      type="email"
                      {...register('emailConfirm')}
                      placeholder="確認のためもう一度ご入力ください"
                    />
                  </span>
                  <FormErrorMessage error={errors.emailConfirm} />
                  <p>
                    ※フリーメールでも構いませんが、@fujimoto.or.jpからのメールを受信できるようにしてください。
                  </p>
                </td>
              </tr>
              <tr>
                <th className="must">電話番号</th>
                <td className="tel">
                  <span>
                    <input
                      type="tel"
                      {...register('tel')}
                      placeholder="例：0986-12-3456"
                    />
                  </span>
                  <p>
                    ※固定電話・携帯電話のどちらでも構いませんが、連絡の取りやすい番号をご記入ください。
                  </p>
                  <FormErrorMessage error={errors.tel} />
                </td>
              </tr>
              <tr>
                <th className="must">高等学校名</th>
                <td className="name">
                  <div className="title_list">
                    <label>
                      <span>学校名</span>
                      <input
                        type="text"
                        {...register('schoolName')}
                        placeholder="例：都城◯◯高校"
                      />
                    </label>
                    <FormErrorMessage error={errors.schoolName} />
                  </div>
                  <div className="radio_btn">
                    <p className="label">どちらかお選びください</p>
                    <span>
                      {graduatedTypes.map((graduated, index) => (
                        <label key={index}>
                          <input
                            type="radio"
                            name="graduatedType"
                            value={graduated}
                            {...register('graduatedType')}
                          />
                          <span>{graduated}</span>
                        </label>
                      ))}
                    </span>
                    <FormErrorMessage error={errors.graduatedType} />
                  </div>
                </td>
              </tr>
              <tr hidden={!showGradeFields}>
                <th className="must">高等学校のご学年</th>
                <td className="radio_btn">
                  {grades.map((grade, index) => (
                    <label key={index}>
                      <input
                        type="radio"
                        name="grade"
                        value={grade}
                        {...register('grade')}
                      />
                      <span>{grade}</span>
                    </label>
                  ))}
                  <FormErrorMessage error={errors.grade} />
                </td>
              </tr>
              <tr>
                <th className="must">オープンキャンパスのご希望日</th>
                <td className="checkbox_square">
                  {openCampusDates.map((date, index) => (
                    <label key={index}>
                      <input
                        type="checkbox"
                        {...register('openCampusDate')}
                        value={date}
                      />
                      <span>{date}</span>
                    </label>
                  ))}
                  <FormErrorMessage error={errors.openCampusDate} />
                </td>
              </tr>
              <tr>
                <th className="must">お問い合わせ内容</th>
                <td className="message">
                  <span>
                    <textarea
                      {...register('inquiryBody')}
                      rows="10"
                      placeholder="お問い合わせをいただく場合はこちらにご入力ください。"
                    ></textarea>
                  </span>
                  <FormErrorMessage error={errors.inquiryBody} />
                </td>
              </tr>
            </tbody>
          </table>
          <div className="recaptcha">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={reCAPTCHASiteKey}
              onChange={onVerifyCaptcha}
            />
            <FormErrorMessage error={errors['g-recaptcha-response']} />
          </div>
          <div className="send_area">
            <p>
              入力された情報は
              <a
                href={privacyPolicyPath}
                target="_blank"
                rel="noreferrer noopener"
              >
                個人情報保護方針
              </a>
              に基づき取り扱われます。
            </p>
            <button input="submit">入力内容を確認する</button>
          </div>
        </form>
      </div>
    </>
  )
}

export { ContactFormSchoolOpenCampusInput }
